import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import {
  HomeIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  CpuChipIcon,
  KeyIcon,
  UsersIcon,
  EnvelopeIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  ViewColumnsIcon,
  DocumentMagnifyingGlassIcon,
  UserIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import DocumentHistory from './DocumentHistory';

const topNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
];

const docsNavItems = [
  { name: 'Upload', href: '/upload', icon: ArrowUpTrayIcon },
  { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
  { name: 'Document Types', href: '/document-types', icon: RectangleStackIcon },
  { name: 'Tool Catalog', href: '/tools', icon: WrenchScrewdriverIcon },
  { name: 'API Keys', href: '/api-keys', icon: KeyIcon },
  { name: 'Recycle Bin', href: '/recycle-bin', icon: TrashIcon },
];

// Combined reference (kept for backward compat)
const navigation = docsNavItems;

const adminNavigation = [
  { name: 'Tenant Management', href: '/admin/tenant-management', icon: UsersIcon },
  { name: 'Connectors', href: '/connectors', icon: PuzzlePieceIcon },
  { name: 'Email Templates', href: '/email-templates', icon: EnvelopeIcon },
  { name: 'Fine-Tuning', href: '/fine-tune', icon: CpuChipIcon },
  { name: 'IDP Pipeline', href: '/idp', icon: SparklesIcon },
  { name: 'API Tokens', href: '/api-tokens', icon: KeyIcon },
  { name: 'API Docs', href: '/admin-api-docs', icon: DocumentTextIcon },
];



const helpNavigation = [
  { name: 'Feedback & Support', href: '/feedback-support', icon: QuestionMarkCircleIcon },
];

export default function LeftSidebar({ collapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { recentDocuments, theme, toggleTheme } = useUIStore();

  const isActive = (href) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarWidth = collapsed ? 'w-16' : 'w-[252px]';
  const isDark = theme === 'dark';

  // Shared link class builder
  const linkClass = (active) => `
    flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
    ${collapsed && active
      ? 'bg-brand-orange/15 text-brand-orange'
      : active
        ? 'bg-gradient-to-r from-brand-orange/20 to-brand-amber/10 text-brand-orange border border-brand-orange/30'
        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
    }
    ${collapsed ? 'justify-center' : ''}
  `;

  const isEnterpriseAdmin = user?.profile?.account_type === 'enterprise_admin';
  const isEnterpriseUser = user?.profile?.account_type === 'enterprise_user';
  const isIndividualUser = !user?.is_superuser && !isEnterpriseAdmin && !isEnterpriseUser;

  const renderNavItems = (items) => items.filter(item => !item.superuserOnly || user?.is_superuser).map(({ name, href, icon: Icon }) => {
    const active = isActive(href);
    return (
      <Link key={name} to={href} className={linkClass(active)} title={collapsed ? name : undefined}>
        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
        {!collapsed && <span className="font-medium text-sm">{name}</span>}
      </Link>
    );
  });

  return (
    <aside
      className={`${sidebarWidth} h-full flex flex-col bg-theme-secondary border-r border-theme-primary transition-all duration-300 ease-in-out`}
    >
      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">

        {/* ── Dashboard ── */}
        <div className="space-y-1 mb-4">
          {renderNavItems(topNavigation)}
        </div>

        {/* ── DOCUMENTS section ── */}
        <div className="pt-4 border-t border-theme-primary">
          {!collapsed && <p className="px-3 mb-2 text-xs font-semibold text-theme-muted uppercase tracking-wider">Documents</p>}
          <div className="space-y-1">
            {docsNavItems.filter(item => !item.superuserOnly || user?.is_superuser).map(({ name, href, icon: Icon }) => {
              const active = isActive(href);
              const isToolCatalog = href === '/tools';

              if (!isToolCatalog) {
                return (
                  <Link key={name} to={href} className={linkClass(active)} title={collapsed ? name : undefined}>
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                    {!collapsed && <span className="font-medium text-sm">{name}</span>}
                  </Link>
                );
              }

              // ── Tool Catalog: simple link (no sub-menu) ──
              return (
                <Link key={name} to={href} className={linkClass(active)} title={collapsed ? name : undefined}>
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="font-medium text-sm">{name}</span>}
                </Link>
              );
            })}

            {/* Upgrade callout for individual users */}
            {isIndividualUser && (
              <Link
                to="/enterprise/upgrade"
                className={`${linkClass(isActive('/enterprise/upgrade'))} mt-1 bg-gradient-to-r from-brand-orange/10 to-brand-amber/5 border border-brand-orange/20 hover:border-brand-orange/40`}
                title={collapsed ? 'Upgrade to Enterprise' : undefined}
              >
                <SparklesIcon className="w-5 h-5 flex-shrink-0 text-brand-orange" />
                {!collapsed && <span className="font-medium text-sm text-brand-orange">Upgrade to Enterprise</span>}
              </Link>
            )}
          </div>
        </div>

        {/* Document History */}
        {!collapsed && !isEnterpriseAdmin && recentDocuments.length > 0 && (
          <DocumentHistory documents={recentDocuments} />
        )}

        {/* ── ANALYTICS section (role-based) ── */}
        <div className="mt-4 pt-4 border-t border-theme-primary">
          {!collapsed && <p className="px-3 mb-2 text-xs font-semibold text-theme-muted uppercase tracking-wider">Analytics</p>}
          <div className="space-y-1">
            {/* My Activity — all users */}
            {(() => {
              const active = isActive('/analytics/my-activity');
              return (
                <Link to="/analytics/my-activity" className={linkClass(active)} title={collapsed ? 'My Activity' : undefined}>
                  <UserIcon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="font-medium text-sm">My Activity</span>}
                </Link>
              );
            })()}

            {/* Organization Overview — enterprise admin only */}
            {isEnterpriseAdmin && (() => {
              const active = isActive('/analytics/org-overview');
              return (
                <Link to="/analytics/org-overview" className={linkClass(active)} title={collapsed ? 'Organization Overview' : undefined}>
                  <BuildingOfficeIcon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="font-medium text-sm">Organization Overview</span>}
                </Link>
              );
            })()}

            {/* Platform Overview — admin/superuser only */}
            {user?.is_superuser && (() => {
              const active = isActive('/analytics/platform-overview');
              return (
                <Link to="/analytics/platform-overview" className={linkClass(active)} title={collapsed ? 'Platform Overview' : undefined}>
                  <ArrowTrendingUpIcon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="font-medium text-sm">Platform Overview</span>}
                </Link>
              );
            })()}
          </div>
        </div>

        {/* System Admin section */}
        {user?.is_superuser && (
          <div className="mt-4 pt-4 border-t border-theme-primary">
            {!collapsed && (
              <p className="px-3 mb-2 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                Admin
              </p>
            )}
            <div className="space-y-1">
              {adminNavigation.map(({ name, href, icon: Icon }) => {
                const active = isActive(href);
                return (
                  <Link key={name} to={href} className={linkClass(active)} title={collapsed ? name : undefined}>
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                    {!collapsed && <span className="font-medium text-sm">{name}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        )}


        {/* ── HELP section ── */}
        <div className="mt-4 pt-4 border-t border-theme-primary">
          {!collapsed && <p className="px-3 mb-2 text-xs font-semibold text-theme-muted uppercase tracking-wider">Help</p>}
          <div className="space-y-1">{renderNavItems(helpNavigation)}</div>
        </div>

      </nav>

      {/* ── Controls ── */}
      <div className="border-t border-theme-primary p-3">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? (isDark ? 'Switch to light mode' : 'Switch to dark mode') : undefined}
        >
          {isDark
            ? <SunIcon className="w-5 h-5 flex-shrink-0 text-brand-amber" />
            : <MoonIcon className="w-5 h-5 flex-shrink-0 text-brand-orange" />
          }
          {!collapsed && (
            <span className="font-medium text-sm">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          )}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-status-error hover:bg-status-error/10 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign out' : undefined}
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="font-medium text-sm">Sign out</span>}
        </button>
      </div>
    </aside>
  );
}